import { Grid, TextField } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import InputMask from "react-input-mask";
import { useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";

import { BackdropCustom } from "../../components/backdrop/Backdrop";
import { ButtonGroup } from "../../components/buttonGroup/ButtonGroup";
import { ButtonTheme } from "../../components/buttonTheme/ButtonTheme";
import { ConfirmationArea } from "../../components/confirmationArea/ConfirmationArea";
import { Form } from "../../components/form/Form";
import { PageCard } from "../../components/pageCard/PageCard";
import { TitleContainer } from "../../components/titleContainer/TitleContainer";
import { useToastr } from "../../components/toast/Toast";
import { useAuth } from "../../hooks/auth";
import { IFormErrors } from "../../interfaces/IFormErrors";
import { IJudgmentType } from "../../interfaces/IJudgmentType";
import { IPageProps } from "../../interfaces/IPageProps";
import api from "../../services/api";
import getValidationError from "../../utils/getValidationError";

export const JudgmentType: React.FC<IPageProps> = ({ pageTitle }) => {
  window.document.title = pageTitle;

  const navigate = useNavigate();
  const location = useLocation();
  const toastr = useToastr();
  const { signOut } = useAuth();

  const [judgmentTypeId, setJudgmentTypeId] = useState("");
  const [id, setId] = useState("");
  const [name, setName] = useState("");
  const [dialogDeleteOpen, setDialogDeleteOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const [formErrors, setFormErrors] = useState<IFormErrors>({});

  const handleListJudgmentType = useCallback(async () => {
    const id = location.pathname.replace("/judgment-type", "").replace("/", "");

    if (id) {
      setJudgmentTypeId(id);
      setLoading(true);

      await api
        .get(`/judgment-types/${id}`)
        .then(response => {
          const judgmentType: IJudgmentType = response.data;

          setName(judgmentType.name ? judgmentType.name : "");

          setId(judgmentType.id ? judgmentType.id.toString() : "");
        })
        .catch(error => {
          if (error.status === 401) {
            signOut();
            navigate("/");
          }

          toastr.error(
            error.response.data?.message || "Contate a equipe de suporte",
          );
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [navigate, signOut, toastr, location.pathname]);

  const handleDeleteJudgmentType = useCallback(async () => {
    const id = location.pathname.replace("/judgment-type", "").replace("/", "");

    if (id) {
      setLoading(true);

      await api
        .delete(`/judgment-types/${id}`)
        .then(() => {
          navigate("/judgment-types");
          toastr.success("Tipo de julgamento excluído com sucesso");
        })
        .catch(error => {
          if (error.status === 401) {
            signOut();
            navigate("/");
          }

          toastr.error(
            error.response.data?.message || "Contate a equipe de suporte",
          );
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [navigate, signOut, toastr, location.pathname]);

  const handleSubmit = useCallback(async () => {
    try {
      setFormErrors({});
      setLoading(true);

      const schema = Yup.object().shape({
        name: Yup.string().required("Nome é obrigatório"),
        id: Yup.number()
          .typeError("Sequência deve ser um número")
          .moreThan(0, "Sequência deve ser maior que 0")
          .lessThan(100, "Sequência deve ser menor que 100")
          .required("Sequência é obrigatório"),
      });

      if (judgmentTypeId) {
        const data: IJudgmentType = {
          id: Number(judgmentTypeId),
          name,
        };

        await schema.validate(data, {
          abortEarly: false,
        });

        await api
          .put(`/judgment-types/${judgmentTypeId}`, data)
          .then(() => {
            toastr.success("Tipo de julgamento atualizado com sucesso");
            navigate("/judgment-types");
          })
          .catch(error => {
            if (error.status === 401) {
              signOut();
              navigate("/");
            }

            toastr.error(
              error.response.data?.message || "Contate a equipe de suporte",
            );
          })
          .finally(() => {
            setLoading(false);
          });
      } else {
        const data: IJudgmentType = {
          id: id ? Number(id) : 0,
          name,
        };

        await schema.validate(data, {
          abortEarly: false,
        });

        await api
          .post(`/judgment-types`, data)
          .then(() => {
            toastr.success("Tipo de julgamento cadastrado com sucesso");
            navigate("/judgment-types");
          })
          .catch(error => {
            if (error.status === 401) {
              signOut();
              navigate("/");
            }

            toastr.error(
              error.response.data?.message || "Contate a equipe de suporte",
            );
          })
          .finally(() => {
            setLoading(false);
          });
      }
    } catch (error: Yup.ValidationError | any) {
      if (error instanceof Yup.ValidationError) {
        const errors = getValidationError(error);
        setFormErrors(errors);
        return;
      }
      toastr.error(
        error.response.data?.message || "Contate a equipe de suporte",
      );
    } finally {
      setLoading(false);
    }
  }, [judgmentTypeId, id, name, navigate, signOut, toastr]);

  const handleRequestNextId = useCallback(async () => {
    setLoading(true);
    await api
      .get(`/judgment-types/next-id`)
      .then(response => {
        const nextId: number = response.data;
        setId(nextId.toString());
      })
      .catch(error => {
        if (error.status === 401) {
          signOut();
          navigate("/");
        } else {
          toastr.error(
            error.response.data?.message || "Contate a equipe de suporte",
          );
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [navigate, signOut, toastr]);

  useEffect(() => {
    const id = location.pathname.replace("/judgment-type", "").replace("/", "");

    if (id) {
      handleListJudgmentType();
    } else {
      handleRequestNextId();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <TitleContainer>
        <h1>Tipo de Julgamento</h1>
      </TitleContainer>
      <PageCard>
        <Form>
          <Grid container spacing={3} sx={{ marginBottom: "24px" }}>
            <Grid item xs={12} md={6} sm={6}>
              <InputMask
                mask={"999"}
                value={id || ""}
                onChange={e => setId(e.target.value)}
                maskPlaceholder={null}
                disabled
              >
                <TextField
                  sx={{ width: "100%" }}
                  size="small"
                  label="id"
                  required
                  helperText={formErrors.id}
                  error={!!formErrors.id}
                />
              </InputMask>
            </Grid>

            <Grid item xs={12} md={6} sm={6}>
              <TextField
                sx={{ width: "100%" }}
                size="small"
                label="Nome"
                value={name}
                required
                onChange={e => setName(e.target.value)}
                error={!!formErrors.name}
                helperText={formErrors.name}
              />
            </Grid>
          </Grid>
          <ButtonGroup justformobilie>
            <ButtonTheme
              onClick={() => navigate("/judgment-types")}
              variant="contained"
              color="inherit"
              disabled={loading}
            >
              Voltar
            </ButtonTheme>

            {judgmentTypeId && (
              <ButtonTheme
                onClick={() => setDialogDeleteOpen(true)}
                variant="contained"
                color="error"
                disabled={loading}
              >
                Excluir
              </ButtonTheme>
            )}

            <ButtonTheme
              onClick={handleSubmit}
              variant="contained"
              disabled={loading}
            >
              Confirmar
            </ButtonTheme>
          </ButtonGroup>
        </Form>

        <ConfirmationArea
          dialogOpen={dialogDeleteOpen}
          id={judgmentTypeId}
          title="Deseja realmente excluir o item?"
          message=""
          key={`dialog-${judgmentTypeId}`}
          handleConfirmation={handleDeleteJudgmentType}
          deny={() => setDialogDeleteOpen(false)}
        />

        {loading && <BackdropCustom />}
      </PageCard>
    </>
  );
};
